import React, { useEffect, useRef, useState } from 'react';

import { validatePermission } from 'actions/index';
import { cooperplaceApi } from 'services/api';
import Card, { DefaultLoadingBodyComponent } from 'v3/components/Card';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';

import IntegrationLogGroup from './IntegrationLogGroup';
import { groupIntegrationLogs } from './utils';

const RESOURCE = {
  driver: 'drivers',
  vehicle: 'vehicles',
  wagon: 'wagons',
  shippingCompany: 'shipping-company',
};

// Slug do cadastro esperado pelos endpoints de reenvio (carg_integration_types e carg_benner_integration_types).
const ENTITY_TYPE = {
  driver: 'DRIVER',
  vehicle: 'VEHICLE',
  wagon: 'WAGON',
  shippingCompany: 'SHIPPING_COMPANY',
};

// Permissão de edição exigida pelo backend para reenviar cada cadastro.
const RESEND_PERMISSION = {
  driver: 'ALTERAR_MOTORISTAS',
  vehicle: 'ALTERAR_VEICULOS',
  wagon: 'EDITAR_IMPLEMENTO',
  shippingCompany: 'EDITAR_TRANSPORTADORA',
};

function resendTo(prefix) {
  return (entity, entityId) =>
    cooperplaceApi.post(`${prefix}/integrations/resend`, {
      type: ENTITY_TYPE[entity],
      id: Number(entityId),
    });
}

// Reenvio por integração. Integrações sem entrada aqui não exibem o botão.
const RESEND_BY_PROVIDER = {
  raster: resendTo('raster'),
  extratta: resendTo('extratta'),
  benner: resendTo('benner'),
  emiteai: resendTo('emiteai'),
};

const DEFAULT_ERROR_MESSAGE = 'Não foi possível carregar o histórico de integrações.';
const DEFAULT_RESEND_ERROR_MESSAGE = 'Não foi possível reenviar a integração.';

// cooperplace-api responde { error }; erros repassados do integration-api vêm como { message }
function getErrorMessage(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || fallback;
}

export default function IntegrationLogs({ entity, entityId, title = 'Integrações', perPage = 10 }) {
  const snackbar = useSnackbar();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const loadedForRef = useRef(null);
  const canResend = validatePermission(RESEND_PERMISSION[entity]);

  useEffect(() => {
    if (!entityId || !RESOURCE[entity]) {
      return undefined;
    }

    let active = true;
    const currentKey = `${entity}-${entityId}`;
    // Recarga do mesmo cadastro (após reenvio) não mostra skeleton, para não recolher as seções
    const silent = loadedForRef.current === currentKey;

    async function fetchLogs() {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        // page/perPage são obrigatórios no backend e paginam o cadastro, não os logs
        const { data } = await cooperplaceApi.get(
          `${RESOURCE[entity]}/${entityId}/integration-logs`,
          { params: { page: 1, perPage: 10 } },
        );
        if (active) {
          loadedForRef.current = currentKey;
          setGroups(groupIntegrationLogs(data));
        }
      } catch (err) {
        if (active && !silent) {
          setGroups([]);
          setError(getErrorMessage(err, DEFAULT_ERROR_MESSAGE));
        }
      } finally {
        if (active && !silent) {
          setLoading(false);
        }
      }
    }

    fetchLogs();

    return () => {
      active = false;
    };
  }, [entity, entityId, refreshKey]);

  async function resend(providerKey) {
    try {
      const { data } = await RESEND_BY_PROVIDER[providerKey](entity, entityId);
      snackbar.show(
        <Text>
          {data?.message || 'Reenvio solicitado.'} O resultado pode levar alguns instantes para
          aparecer no histórico.
        </Text>,
        { type: 'success' },
      );
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      snackbar.show(<Text>{getErrorMessage(err, DEFAULT_RESEND_ERROR_MESSAGE)}</Text>, {
        type: 'error',
      });
    }
  }

  function renderBody() {
    if (error) {
      return (
        <Text type="regular" color="danger">
          {error}
        </Text>
      );
    }

    if (!groups.length) {
      return (
        <Text type="regular" color="gray">
          Nenhuma integração registrada
        </Text>
      );
    }

    return groups.map((group) => (
      <IntegrationLogGroup
        key={`${entityId}-${group.key}`}
        groupKey={group.key}
        label={group.label}
        logs={group.logs}
        perPage={perPage}
        onResend={canResend && RESEND_BY_PROVIDER[group.key] ? () => resend(group.key) : undefined}
      />
    ));
  }

  return (
    <Card
      loading={!entityId || loading}
      LoadingBodyComponent={() => <DefaultLoadingBodyComponent linesCount={3} perLine={1} />}
      header={
        <Text color="dark" type="header">
          {title}
        </Text>
      }
    >
      {renderBody()}
    </Card>
  );
}
